import { config } from '@static/js/env.config';
import { GoogleMapsProps, GuestPartnerHotelBookingRedirectParams, PricingInfoProps, RedirectMechanismProps } from '../../HotelBooking.types';
import { ensureHttpsPrefix } from '@shared/utils/urlHelpers';
import { mergeUtmParameters, PartialUtmParameters, applyUtmParametersToUrl } from '@shared/utils/utmParameters';
import { NuiteeLanguage } from '@shared/hooks/useNuiteeLanguage';
import { ExtendedDiscount, useHotelCouponCode } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';

interface RedirectTelemetryArgs {
  clientReference?: string;
  redirectData?: RedirectMechanismProps;
  pricingInfo?: PricingInfoProps | null;
  googleMaps?: GoogleMapsProps | null;
  telemetryName?: GuestPartnerHotelBookingRedirectParams['telemetryName'];
  source?: GuestPartnerHotelBookingRedirectParams['source'];
  utmParams?: {
    utm_source?: string;
    utm_source_secondary?: string;
    utm_medium?: string;
    utm_campaign?: string;
  };
  hasHotelSearchPageUrl?: boolean;
}

interface ConvertToNuiteeFallbackUrlParams
  extends Readonly<{
    xData?: string;
    currency: string;
    joyPlaceId: string;
    name?: string | null;
    clientReference: string;
    placeId?: string | null;
    language: NuiteeLanguage;
    checkInCheckoutDates: CheckInCheckoutDates;
  }> {}

interface ConvertToBERedirectUrlParams
  extends Readonly<{
    xData?: string;
    currency: string;
    name?: string | null;
    clientReference: string;
    language: NuiteeLanguage;
    placeId?: string | null;
    redirectData: RedirectMechanismProps;
  }> {}

interface AddCommonUrlParamsArgs
  extends Readonly<{
    url: URL;
    xData?: string;
    name?: string | null;
    placeId?: string | null;
    language?: NuiteeLanguage;
  }> {}

/**
 * Generates telemetry payload for hotel booking redirection.
 *
 * @param {RedirectTelemetryArgs} args - Input arguments containing hotel data and context.
 * @returns {Partial<GuestPartnerHotelBookingRedirectParams>} Telemetry payload.
 */
export const convertToRedirectTelemetry = (args: RedirectTelemetryArgs) => {
  const { clientReference, utmParams, source, telemetryName, hasHotelSearchPageUrl, redirectData, pricingInfo, googleMaps } = args;

  const { lat, lng, gplid, name, address, zentrumHubId } = googleMaps || {};
  const destinationName = redirectData?.telemetryDestinationName ?? 'notFound';

  const telemetryPayload: Partial<GuestPartnerHotelBookingRedirectParams> = {
    telemetryName,
    clientReference,
    source,
    ...utmParams,
    ...(hasHotelSearchPageUrl && { hotelSearchPageUrlType: 'hotelSoldOut' }),
    redirectMechanismType: redirectData?.type,
    hotelName: name,
    hotelPriceCurrency: pricingInfo?.nightlyBaseRate.currency,
    hotelPrice: pricingInfo?.nightlyBaseRate.floatingPointDecimalString,
    hotelStrikeOutPrice: pricingInfo?.strikeoutNightlyBaseRate?.floatingPointDecimalString,
    destinationName,
    zentrumHub: zentrumHubId ? { id: zentrumHubId } : undefined,
    googleMaps:
      gplid || lat || lng || name || address
        ? {
            gplid: gplid ?? undefined,
            name: name ?? undefined,
            address: address ?? undefined,
            lat: lat?.toString(),
            lng: lng?.toString(),
          }
        : undefined,
  };

  return telemetryPayload;
};

/**
 * Builds a custom or default hotel redirection URL.
 *
 * @param hotelWebsite - Raw hotel website URL.
 * @param clientReference - Unique client tracking ID.
 * @param currency - Currency string (e.g., 'USD').
 * @param redirectData - Redirect tracking metadata.
 * @param hasHotelSearchPageUrl - Whether the redirect was triggered by an hotel search page dialog.
 * @param currentPageUtmParams - UTM params from current page URL (highest priority - overrides everything).
 * @returns Object with constructed hotel URL and final redirect URL.
 */
export const convertToCustomOrHotelUrl = (
  hotelWebsite: string,
  clientReference: string,
  currency: string,
  redirectData?: RedirectMechanismProps,
  hasHotelSearchPageUrl?: boolean,
  currentPageUtmParams?: PartialUtmParameters,
) => {
  const hotelUrl = new URL(ensureHttpsPrefix(hotelWebsite));
  if (hasHotelSearchPageUrl) {
    // Convert current page UTM params to UtmParameters format (filter out null/undefined)
    const currentPageUtm: PartialUtmParameters = currentPageUtmParams || {};

    // Merge UTM params with priority: currentPageUtm > hotelWebsiteUtm > internal defaults
    const internalDefaults: PartialUtmParameters = {
      utm_medium: 'web',
      utm_source: 'website_hotel_redirect_page',
      utm_campaign: 'hotel_sold_out',
    };
    const hotelWebsiteUtm = extractUtmParametersFromUrl(hotelWebsite);

    // Merge with priority: currentPageUtm (highest) > hotelWebsiteUtm > internalDefaults (lowest)
    const mergedUtm = mergeUtmParameters(currentPageUtm, mergeUtmParameters(hotelWebsiteUtm, internalDefaults));

    // Apply merged UTM params to URL using utility function
    const urlWithUtm = applyUtmParametersToUrl(hotelUrl.toString(), mergedUtm);
    const hotelUrlWithUtm = new URL(urlWithUtm);

    // Set other params (clientReference, currency, utm_source_secondary)
    hotelUrlWithUtm.searchParams.set(redirectData?.trackingQueryStringKey || 'clientReference', clientReference);
    hotelUrlWithUtm.searchParams.set('currency', currency);
    hotelUrlWithUtm.searchParams.set('utm_source_secondary', 'accommodationsPlace_hotelDetails');

    return hotelUrlWithUtm;
  }
  return hotelUrl;
};

// Local helper to extract UTM parameters from a URL string; kept here because usage is limited to this module
const extractUtmParametersFromUrl = (url: string): PartialUtmParameters | undefined => {
  try {
    const urlObj = new URL(url);
    const utm_medium = urlObj.searchParams.get('utm_medium');
    const utm_source = urlObj.searchParams.get('utm_source');
    const utm_campaign = urlObj.searchParams.get('utm_campaign');

    if (!utm_medium && !utm_source && !utm_campaign) {
      return undefined;
    }

    const params: { utm_medium?: 'web' | 'email' | 'text'; utm_source?: string; utm_campaign?: string } = {};
    if (utm_medium && ['web', 'email', 'text'].includes(utm_medium)) {
      params.utm_medium = utm_medium as 'web' | 'email' | 'text';
    }
    if (utm_source) {
      params.utm_source = utm_source;
    }
    if (utm_campaign) {
      params.utm_campaign = utm_campaign;
    }
    return params as PartialUtmParameters;
  } catch {
    try {
      const searchParams = new URLSearchParams(url.split('?')[1]);
      const utm_medium = searchParams.get('utm_medium');
      const utm_source = searchParams.get('utm_source');
      const utm_campaign = searchParams.get('utm_campaign');

      if (!utm_medium && !utm_source && !utm_campaign) {
        return undefined;
      }

      const params: { utm_medium?: 'web' | 'email' | 'text'; utm_source?: string; utm_campaign?: string } = {};
      if (utm_medium && ['web', 'email', 'text'].includes(utm_medium)) {
        params.utm_medium = utm_medium as 'web' | 'email' | 'text';
      }
      if (utm_source) {
        params.utm_source = utm_source;
      }
      if (utm_campaign) {
        params.utm_campaign = utm_campaign;
      }
      return params as PartialUtmParameters;
    } catch {
      // Swallow parsing errors here; upstream callers treat undefined as "no UTM"
      return undefined;
    }
  }
};

/**
 * Adds common parameters (placeId, name, xData, language) to a URL.
 * Centralizes parameter addition logic for consistency across redirect functions.
 * Modifies the URL object in place.
 *
 * @param params - Parameters to add to the URL
 * @param params.url - The URL object to modify (modified in place)
 * @param params.placeId - Optional Google Place ID
 * @param params.name - Optional place name
 * @param params.xData - Optional encoded discount data
 * @param params.language - Optional language parameter
 */
const addCommonUrlParams = ({ url, placeId, name, xData, language }: AddCommonUrlParamsArgs): void => {
  if (placeId) {
    url.searchParams.set('placeId', placeId);
  }
  if (name) {
    url.searchParams.set('name', name);
  }
  if (xData) {
    url.searchParams.set('xData', xData);
  }
  if (language) {
    url.searchParams.set('language', language);
  }
};

/**
 * Builds a redirect URL with tracking parameters from BE redirect logic.
 *
 * @param redirectData - Metadata for redirect destination.
 * @param clientReference - Tracking reference for client.
 * @param currency - Currency string.
 * @param language - Language string.
 * @param xData - Optional tracking data.
 * @param placeId - Optional Google Place ID.
 * @param name - Optional place name.
 * @returns Final URL object.
 */
export const convertToBERedirectUrl = ({ redirectData, clientReference, currency, language, xData, placeId, name }: ConvertToBERedirectUrlParams) => {
  const redirectUrl = new URL(redirectData.url);
  redirectUrl.searchParams.set(redirectData?.trackingQueryStringKey || 'clientReference', clientReference);
  redirectUrl.searchParams.set('currency', currency);
  // Add query language param only for nuitee
  if (redirectData.telemetryDestinationName === 'stays' || redirectData.telemetryDestinationName === 'externalPDPUrl') {
    addCommonUrlParams({ url: redirectUrl, placeId, name, xData, language });
  }

  /**
   * Ensure clientReference is always present in the redirect URL.
   *
   * For Nuitee (stays.withjoy.com): backend sets trackingQueryStringKey="clientReference" (already added above).
   * For ZentrumHub (hotels.withjoy.com): backend sets trackingQueryStringKey="campaignId" (not clientReference).
   *
   * Without clientReference, users are redirected to "CUG not verified" page even when coming from valid event links.
   * This check ensures clientReference is present for both Nuitee and ZentrumHub white labels.
   */
  if (!redirectUrl.searchParams.has('clientReference') && clientReference) {
    redirectUrl.searchParams.set('clientReference', clientReference);
  }

  return redirectUrl;
};
interface CheckInCheckoutDates {
  checkIn: string;
  checkOut: string;
  isCheckInOrCheckoutValid: boolean;
  message: string;
}

/**
 * Builds ZentrumHub fallback URL with optional check-in/out dates.
 *
 * @param joyPlaceId - Place identifier.
 * @param clientReference - Campaign ID.
 * @param currency - Currency string.
 * @param checkInCheckoutDates - CheckInCheckoutDates
 * @returns Final URL object.
 */
export const convertToZhFallbackUrl = (joyPlaceId: string, clientReference: string, currency: string, checkInCheckoutDates: CheckInCheckoutDates) => {
  const { checkIn: checkInDate, checkOut: checkOutDate, isCheckInOrCheckoutValid } = checkInCheckoutDates;
  const zhUrl = new URL(`${config.zentrumhubWhitelableBaseUrl}/${joyPlaceId}`);
  zhUrl.searchParams.set('campaignid', clientReference);

  // Return if the check-in or check-out is valid (within 11 months) - Mostly used for zh, where zh shows empty results if date greater than 11 months.
  if (isCheckInOrCheckoutValid) {
    zhUrl.searchParams.set('checkin', checkInDate);
    zhUrl.searchParams.set('checkout', checkOutDate);
  }

  if (currency) {
    zhUrl.searchParams.set('currency', currency);
  }

  return zhUrl;
};

/**
 * Builds Nuitee fallback URL with check-in/out and currency.
 *
 * @param params - Parameters for building Nuitee fallback URL
 * @param params.joyPlaceId - Place identifier.
 * @param params.clientReference - Client reference string.
 * @param params.currency - Currency string.
 * @param params.language - Language string.
 * @param params.checkInCheckoutDates - CheckInCheckoutDates
 * @param params.xData - Optional encoded discount data.
 * @param params.placeId - Optional Google Place ID.
 * @param params.name - Optional place name.
 * @returns Final URL object.
 */
export const convertToNuiteeFallbackUrl = ({ joyPlaceId, clientReference, currency, language, checkInCheckoutDates, xData, placeId, name }: ConvertToNuiteeFallbackUrlParams) => {
  const { checkIn: checkInDate, checkOut: checkOutDate } = checkInCheckoutDates;
  const nuiteeUrl = new URL(`${config.nuiteeWhitelableBaseUrl}/${joyPlaceId}`);

  nuiteeUrl.searchParams.set('clientReference', clientReference);
  nuiteeUrl.searchParams.set('checkin', checkInDate);
  nuiteeUrl.searchParams.set('checkout', checkOutDate);

  if (currency) {
    nuiteeUrl.searchParams.set('currency', currency);
  }

  addCommonUrlParams({ url: nuiteeUrl, placeId, name, xData, language });

  return nuiteeUrl;
};

export const useDiscountCodeForRedirect = (destination: 'nuitee' | 'native', discountCode: string | undefined) => {
  const discountInfo = useHotelCouponCode(discountCode);
  return destination === 'nuitee' ? discountInfo?.partnerCodes.find(code => code.partnerId === 'nuitee')?.code : discountInfo?.code;
};

export const joyHotelDiscountCodes = (discount: ExtendedDiscount | undefined) => {
  const nuiteeCode = discount?.partnerCodes.find(code => code.partnerId === 'nuitee')?.code;
  const nativeCode = discount?.code;
  return { nuiteeCode, nativeCode };
};
